import { Box, Link } from "@mui/material";
import { Button } from "@samdesk/components/Button";
import { Checkbox } from "@samdesk/components/Checkbox";
import { Form } from "@samdesk/components/Form";
import { Heading } from "@samdesk/components/Heading";
import { InputText } from "@samdesk/components/InputText";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import { ResetPasswordRequest } from "autoGenSubTypes";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { type FieldValues, useFormContext, useWatch } from "react-hook-form";
import { NavigateFunction, Link as RouterLink } from "react-router-dom";
import { messageSx, linkSx, wrapperSx } from "router/authStyles";
import CenteredCircularProgress from "ui-library/CenteredCircularProgress";
import { PasswordCriteria } from "ui-library/PasswordCriteria";

interface Props {
	handleSubmitResetPasswordRequest: (data: FieldValues) => void;
	isInitialLoading: boolean;
	navigate: NavigateFunction;
	password: string;
	passwordValidationDetails: Record<string, boolean> | null;
	resetPasswordRequest: ResetPasswordRequest;
	setPassword: Dispatch<SetStateAction<string>>;
	submitError: string;
	validateNewPassword: (value: string) => true | string;
}

const RevalidateConfirmPassword = () => {
	const { getValues, trigger } = useFormContext();
	const password = useWatch({ name: "password" });

	useEffect(() => {
		if (getValues("confirmPassword")) trigger("confirmPassword");
	}, [getValues, password, trigger]);

	return null;
};

const ResetPassword = ({
	handleSubmitResetPasswordRequest,
	isInitialLoading,
	navigate,
	password,
	passwordValidationDetails,
	resetPasswordRequest,
	setPassword,
	submitError,
	validateNewPassword,
}: Props) => {
	const [showPassword, setShowPassword] = useState(false);
	const passwordType = showPassword ? "text" : "password";

	if (isInitialLoading) return <CenteredCircularProgress />;

	if (resetPasswordRequest) {
		return (
			<Box sx={wrapperSx}>
				<Stack spacing="large">
					<Heading level="1">Reset your password</Heading>

					<Form onSubmit={handleSubmitResetPasswordRequest}>
						<RevalidateConfirmPassword />
						<Stack spacing="large">
							<Stack spacing="base">
								<InputText
									autoComplete="new-password"
									label="New password"
									name="password"
									onChange={setPassword}
									rules={{
										required: "New password is required",
										validate: validateNewPassword,
									}}
									type={passwordType}
								/>
								<InputText
									autoComplete="new-password"
									label="Confirm new password"
									name="confirmPassword"
									rules={{
										required: "Confirm your new password",
										validate: (value, formValues) =>
											value === formValues.password ||
											"Your passwords must match",
									}}
									type={passwordType}
								/>
								{submitError && (
									<Box sx={messageSx}>
										<Text accent="error">{submitError}</Text>
									</Box>
								)}
							</Stack>
							<Box sx={buttonRowSx}>
								<Checkbox
									checked={showPassword}
									label="Show password"
									name="showPassword"
									onChange={setShowPassword}
								/>
								<Button label="Change password" prominence="primary" submit />
							</Box>
						</Stack>
					</Form>

					{resetPasswordRequest.account_password_policy && (
						<Box sx={passwordPolicyWrapperSx}>
							<Stack spacing="small">
								<Heading level="4">Password policy</Heading>
								<PasswordCriteria
									newPassword={password}
									passwordPolicy={resetPasswordRequest.account_password_policy}
									passwordValidationDetails={passwordValidationDetails}
								/>
							</Stack>
						</Box>
					)}
				</Stack>
			</Box>
		);
	}

	/** For case when for whatever reason we could not find the request
	 *  Give the user an easy way to navigate back */
	return (
		<Box sx={wrapperSx}>
			<Stack spacing="large">
				<Heading level="1" multiline>
					Reset password request could not be found. Please try again.
				</Heading>
				<Button
					fullWidth
					label="Create new request"
					onClick={() => navigate("/reset_password")}
					prominence="primary"
				/>
				<Stack alignItems="center">
					<Link
						component={RouterLink}
						replace
						sx={linkSx}
						to="/signin"
						underline="none"
					>
						I remember my password
					</Link>
				</Stack>
			</Stack>
		</Box>
	);
};

export default ResetPassword;

/** Style */

const buttonRowSx = {
	alignItems: "center",
	display: "flex",
	justifyContent: "space-between",
};

const passwordPolicyWrapperSx = {
	backgroundColor: "grey.800",
	borderRadius: 2,
	padding: "25px 25px 15px 25px",
};
